/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../../components/feature/Breadcrumb';
import AstoriaHero from './components/AstoriaHero';
import BrokerValueProp from './components/BrokerValueProp';
import AstoriaInventory from './components/AstoriaInventory';
import AstoriaLocalGuides from './components/AstoriaLocalGuides';
import WhyAstoriaBuyers from './components/WhyAstoriaBuyers';
import AstoriaContactForm from './components/AstoriaContactForm';
import { useSEOHead } from '../../hooks/useSEOHead';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';
import { injectFAQSchema, removeFAQSchema } from '../../lib/safeSchema';
import { pageFaqs } from '../../mocks/pageFaqs';

export default function UsedCarsAstoriaPage() {
  useSEOHead({
    title: 'Used Cars Astoria OR — No Dealer Markup | Rockhound Auto',
    description: 'Used cars Astoria Oregon with free delivery. Wholesale pricing, no dealership games, transparent broker model. 15 minutes from Warrenton. Buy a car without the circus.',
    keywords: 'used cars astoria oregon, used cars astoria, cars for sale astoria oregon, used car dealer astoria, car broker astoria, free delivery astoria, rockhound auto',
    canonicalUrl: 'https://rockhoundauto.com/used-cars-astoria-oregon',
    geoPosition: '46.1879;-123.8313',
    geoRegion: 'US-OR',
    geoPlacename: 'Astoria, Oregon',
  });

  useEffect(() => {
    // Inject FAQPage schema
    injectFAQSchema(
      'schema-astoria-faq',
      pageFaqs.usedCarsAstoriaOregon,
      'https://rockhoundauto.com/used-cars-astoria-oregon#faqpage'
    );

    // Inject LocalBusiness schema
    const businessSchema = {
      '@context': 'https://schema.org',
      '@type': 'AutoDealer',
      '@id': 'https://rockhoundauto.com/#organization',
      name: 'Rockhound Auto',
      description: 'Trusted used car dealer serving Astoria, Oregon with transparent pricing and quality vehicles',
      url: 'https://rockhoundauto.com',
      telephone: '+1-503-468-3000',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '1025 SE Marlin Ave',
        addressLocality: 'Warrenton',
        addressRegion: 'OR',
        postalCode: '97146',
        addressCountry: 'US'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 46.1651,
        longitude: -123.9237
      },
      areaServed: [
        {
          '@type': 'City',
          name: 'Astoria',
          '@id': 'https://rockhoundauto.com/used-cars-astoria-oregon'
        },
        {
          '@type': 'City',
          name: 'Warrenton'
        },
        {
          '@type': 'City',
          name: 'Seaside'
        }
      ],
      priceRange: '$$',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '18:00'
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Saturday',
          opens: '10:00',
          closes: '16:00'
        }
      ]
    };
    injectJsonLd('schema-astoria-business', businessSchema);

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Used Cars',
          item: 'https://rockhoundauto.com/inventory'
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Astoria Oregon',
          item: 'https://rockhoundauto.com/used-cars-astoria-oregon'
        }
      ]
    };

    injectJsonLd('schema-astoria-breadcrumb', breadcrumbSchema);

    return () => {
      removeFAQSchema('schema-astoria-faq');
      removeJsonLd('schema-astoria-business');
      removeJsonLd('schema-astoria-breadcrumb');
    };
  }, []);

  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Used Cars Astoria Oregon', path: '/used-cars-astoria-oregon' }
        ]}
      />

      <AstoriaHero />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Dealer Insight Block */}
        <div className="mb-16 bg-gradient-to-br from-teal-50 to-cyan-50 border-l-4 border-teal-500 rounded-lg p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                <i className="ri-map-pin-line text-2xl text-white"></i>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Dealer Insight</h3>
              <p className="text-gray-700 leading-relaxed">
                Astoria buyers face limited local inventory and higher prices at franchise dealers. Driving 15 minutes to Warrenton opens access to better selection and transparent pricing. We see Astoria customers weekly who tell us they wish they'd come here first instead of wasting time at overpriced lots closer to home. Browse our <Link to="/inventory" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900 transition-colors">used cars near Astoria</Link> to see what's available now.
              </p>
            </div>
          </div>
        </div>

        <WhyAstoriaBuyers />
        <BrokerValueProp />
        <AstoriaInventory />
        <AstoriaLocalGuides />
        <AstoriaContactForm />
      </div>

      {/* Cross-link to Service Area page */}
      <section className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-map-2-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Rockhound Auto Serves Astoria, Oregon
                </h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  See full service details, nearby cities, and delivery coverage for the Astoria area.
                </p>
              </div>
            </div>
            <Link
              to="/service-areas/astoria"
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <i className="ri-map-pin-line"></i>
              Astoria Service Area Details
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
